import { useEffect, useState } from 'react';
import { SellerModalityCard, COMMERCIAL_MSG } from '@/components/SellerModalityCard';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useSEO } from '@/lib/useSEO';
import { formatCOP, formatDate, statusLabel, statusColor } from '@/lib/format';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Package, ShoppingBag, Wallet, BarChart3, AlertCircle, Sparkles, CreditCard, PackageX, PackageMinus, TrendingUp, Award, CheckCircle2, ArrowRight } from 'lucide-react';
import { SELLER } from '@/constants/testIds';
import { SellerSalesChart } from '@/components/SellerSalesChart';

const PLAN_BADGE = {
  pro: { label: 'PRO', className: 'bg-orange-500 text-slate-900' },
  basic: { label: 'BÁSICO', className: 'bg-slate-900 text-white' },
  none: { label: 'SIN PLAN', className: 'bg-slate-200 text-slate-700' },
};

export default function SellerDashboard() {
  const { user, sellerProfile } = useAuth();
  const [data, setData] = useState(null);

  useSEO({
    title: 'Panel del vendedor | AutoMarket Colombia',
    description: 'Resumen de ventas, comisiones, pedidos y productos publicados.',
    canonical: '/vendedor',
  });

  const commercial = sellerProfile?.commercial;
  const canSell = !!commercial?.can_sell;
  const approved = sellerProfile?.status === 'approved';
  useEffect(() => {
    if (approved) {
      api.get('/sellers/me/dashboard').then(({ data }) => setData(data)).catch(() => {});
    }
  }, [approved]);

  const plan = canSell ? (commercial?.plan?.plan || 'none') : 'none';
  const planBadge = PLAN_BADGE[plan] || PLAN_BADGE.none;

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Panel del vendedor</div>
            <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1 flex items-center gap-3 flex-wrap">
              {sellerProfile?.business_name || user?.name}
              <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-1 ${planBadge.className}`}>
                {plan === 'pro' && <Sparkles className="h-2.5 w-2.5" />}
                {planBadge.label}
              </span>
            </h1>
          </div>
        </div>

        {/* Setup blockers */}
        {!canSell && (
          <div className="mt-8 border-2 border-orange-500 bg-orange-50 p-6 flex items-start gap-4">
            <AlertCircle className="h-6 w-6 text-orange-600 shrink-0" />
            <div className="flex-1">
              <div className="font-display text-lg font-bold text-slate-900">
                {!approved ? 'Cuenta en revisión' : (commercial?.reason === 'plan_expired' ? 'Modalidad vencida' : 'Selecciona tu modalidad')}
              </div>
              <p className="text-slate-700 text-sm mt-1">
                {sellerProfile?.status === 'incomplete' && 'Completa tu onboarding para enviar la solicitud.'}
                {sellerProfile?.status === 'pending' && 'Tu solicitud está siendo revisada por el administrador.'}
                {sellerProfile?.status === 'rejected' && 'Tu solicitud fue rechazada. Contacta soporte.'}
                {approved && COMMERCIAL_MSG[commercial?.reason]}
              </p>
              <Link to={approved ? '/vendedor/suscripcion' : '/vendedor/onboarding'} className="inline-block mt-3">
                <Button className="rounded-none bg-slate-900 hover:bg-slate-800" data-testid="seller-blocker-continue-btn">
                  {approved ? 'Elegir modalidad' : 'Continuar'}
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* KPIs */}
        {data && (
          <>
            {/* Iter 45 — Card vigencia activación + plan */}
            <SellerModalityCard commercial={data.lifecycle?.commercial || commercial} />
            <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4">
              <Kpi testid={SELLER.kpiSales} icon={Wallet} label="Ventas brutas" value={formatCOP(data.kpis.sales_total)} />
              <Kpi testid={SELLER.kpiCommission} icon={BarChart3} label="Comisión plataforma" value={formatCOP(data.kpis.commission_total)} />
              <Kpi testid={SELLER.kpiNet} icon={Wallet} label="Neto vendedor" value={formatCOP(data.kpis.net_total)} accent />
              <Kpi testid={SELLER.kpiOrders} icon={ShoppingBag} label="Pedidos" value={data.kpis.orders} />
              <Kpi testid={SELLER.kpiProducts} icon={Package} label="Productos" value={data.kpis.products} />
            </div>

            {/* Accesos rápidos — el vendedor entra al panel y ve todo lo que puede hacer */}
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="seller-dashboard-quicklinks">
              {[
                { to: '/vendedor/productos', label: 'Mis productos', testid: 'seller-tile-products' },
                { to: '/vendedor/productos?new=1', label: 'Crear producto', testid: 'seller-tile-new-product' },
                { to: '/vendedor/productos?bulk=1', label: 'Cargue masivo', testid: 'seller-tile-bulk' },
                { to: '/vendedor/pedidos', label: 'Ventas / pedidos', testid: 'seller-tile-orders' },
                { to: '/vendedor/envios', label: 'Envíos y tarifas', testid: 'seller-tile-shipping' },
                { to: '/vendedor/wallet', label: 'Saldo e ingresos', testid: 'seller-tile-wallet' },
                { to: '/vendedor/wallet#retiros', label: 'Retiros', testid: 'seller-tile-withdrawals' },
                { to: '/vendedor/mensajes', label: 'Mensajes', testid: 'seller-tile-messages' },
                { to: '/vendedor/productos?promo=1', label: 'Promociones', testid: 'seller-tile-promotions' },
                { to: '/vendedor/finanzas', label: 'Estadísticas', testid: 'seller-tile-stats' },
                { to: '/mis-pqr', label: 'PQR', testid: 'seller-tile-pqr' },
                { to: '/perfil', label: 'Perfil', testid: 'seller-tile-profile' },
                { to: '/vendedor/suscripcion', label: 'Plan y suscripción', testid: 'seller-tile-subscription' },
              ].map((t) => (
                <Link
                  key={t.to}
                  to={t.to}
                  data-testid={t.testid}
                  className="border border-slate-200 bg-white px-3 py-3 text-sm font-semibold text-slate-900 hover:border-slate-900 text-center"
                >
                  {t.label}
                </Link>
              ))}
            </div>

            {/* Alertas de inventario y productos destacados */}
            <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Kpi
                testid="seller-kpi-out-of-stock"
                icon={PackageX}
                label="Agotados"
                value={data.kpis.out_of_stock || 0}
                accent={data.kpis.out_of_stock > 0 ? 'danger' : null}
              />
              <Kpi
                testid="seller-kpi-low-stock"
                icon={PackageMinus}
                label="Próximos a agotarse"
                value={data.kpis.low_stock || 0}
                accent={data.kpis.low_stock > 0 ? 'warn' : null}
                hint="Stock ≤ 3"
              />
              <ProductKpi
                testid="seller-kpi-top-selling"
                icon={TrendingUp}
                label="Más vendido"
                product={data.kpis.top_selling_product}
                metric={data.kpis.top_selling_product ? `${data.kpis.top_selling_product.qty_sold} und` : null}
              />
              <ProductKpi
                testid="seller-kpi-top-profit"
                icon={Award}
                label="Más rentable"
                product={data.kpis.top_profit_product}
                metric={data.kpis.top_profit_product ? formatCOP(data.kpis.top_profit_product.net) : null}
              />
            </div>

            {/* Profile completeness */}
            {data.profile_completeness && (
              <div className="mt-6 border border-slate-200 bg-white p-5" data-testid="seller-profile-completeness">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 flex items-center gap-1.5">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Perfil de la tienda
                    </div>
                    <div className="mt-1 font-display text-2xl font-black text-slate-900">
                      Perfil {data.profile_completeness.score}% listo
                      <span className="text-slate-500 text-base font-semibold ml-2">
                        {data.profile_completeness.done}/{data.profile_completeness.total}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Un perfil completo genera más confianza en los compradores y mejora tu posicionamiento.
                    </p>
                  </div>
                  {data.profile_completeness.score < 100 && (
                    <Link to="/perfil" data-testid="seller-completeness-cta">
                      <Button variant="outline" className="rounded-none border-orange-500 text-orange-700 hover:bg-orange-500 hover:text-slate-900">
                        Completar perfil <ArrowRight className="h-3.5 w-3.5 ml-1" />
                      </Button>
                    </Link>
                  )}
                </div>
                {/* Progress bar */}
                <div className="mt-4 h-2 bg-slate-100 overflow-hidden">
                  <div
                    className={`h-full transition-all ${data.profile_completeness.score === 100 ? 'bg-emerald-500' : 'bg-orange-500'}`}
                    style={{ width: `${data.profile_completeness.score}%` }}
                    data-testid="seller-completeness-bar"
                  />
                </div>
                {data.profile_completeness.missing.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5" data-testid="seller-completeness-missing">
                    {data.profile_completeness.missing.map((m) => (
                      <span key={m} className="text-[10px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-700 px-2 py-1 border border-slate-200">
                        {m}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Plan upgrade CTA */}
            {plan !== 'pro' && (
              <div className="mt-6 border border-orange-300 bg-orange-50 p-4 flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <CreditCard className="h-5 w-5 text-orange-600" />
                  <div>
                    <div className="font-display font-bold text-slate-900 text-sm">
                      {plan === 'none' ? 'Reduce tu comisión con un plan' : 'Mejora a PRO y destaca tu tienda'}
                    </div>
                    <div className="text-xs text-slate-600">
                      {plan === 'none' ? 'Plan Básico o PRO: comisiones más bajas + beneficios extra.' :
                        'PRO: comisión mínima + productos destacados + badge PRO.'}
                    </div>
                  </div>
                </div>
                <Link to="/vendedor/suscripcion">
                  <Button className="rounded-none bg-orange-500 hover:bg-orange-400 text-slate-900 font-bold">
                    Ver planes
                  </Button>
                </Link>
              </div>
            )}

            <div className="mt-8">
              <SellerSalesChart />
            </div>

            <div className="mt-8">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Pedidos recientes</div>
                <Link to="/vendedor/pedidos" className="text-sm font-semibold text-blue-700 hover:underline">
                  Ver todos →
                </Link>
              </div>
              <div className="border border-slate-200 bg-white overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="px-4 py-3">Pedido</th>
                      <th className="px-4 py-3">Comprador</th>
                      <th className="px-4 py-3">Fecha</th>
                      <th className="px-4 py-3">Total</th>
                      <th className="px-4 py-3">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.recent_orders.length === 0 && (
                      <tr><td colSpan={5} className="p-8 text-center text-slate-500">Sin pedidos aún</td></tr>
                    )}
                    {data.recent_orders.map((o) => (
                      <tr key={o.order_id} className="row-hover">
                        <td className="px-4 py-3 font-mono text-xs">{o.order_id}</td>
                        <td className="px-4 py-3">{o.buyer_name}</td>
                        <td className="px-4 py-3 text-slate-500">{formatDate(o.created_at)}</td>
                        <td className="px-4 py-3 font-bold">{formatCOP(o.total)}</td>
                        <td className="px-4 py-3"><Badge className={`rounded-none border ${statusColor[o.status]}`}>{statusLabel[o.status] || o.status}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

const Kpi = ({ icon: Icon, label, value, testid, accent, hint }) => {
  const style =
    accent === 'danger' ? 'border-rose-400 bg-rose-50' :
    accent === 'warn' ? 'border-amber-400 bg-amber-50' :
    accent ? 'border-orange-500 bg-orange-50' :
    'border-slate-200 bg-white';
  return (
    <div data-testid={testid} className={`border p-4 ${style}`}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-slate-500" />
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
      </div>
      <div className="mt-2 font-display text-2xl font-black text-slate-900">{value}</div>
      {hint && <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
};

const ProductKpi = ({ icon: Icon, label, product, metric, testid }) => (
  <div data-testid={testid} className="border border-slate-200 bg-white p-4">
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-slate-500" />
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
    </div>
    {product ? (
      <div className="mt-2">
        <div className="font-semibold text-slate-900 text-sm leading-tight line-clamp-2" title={product.name}>
          {product.name}
        </div>
        <div className="mt-1 text-xs font-bold text-orange-600">{metric}</div>
      </div>
    ) : (
      <div className="mt-2 text-slate-400 text-sm">Aún sin ventas</div>
    )}
  </div>
);


